import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Button, Text, Input, LinearProgress } from '@mafc/trolly';
import COLORS from 'theme/colors';
import { ReactComponent as IconChecked } from 'assets/svgs/checked.svg';
import TYPOGRAPHY from 'theme/typography';
import { isRTL } from 'config/localisation';

const { Paragraph } = Text;

interface IProps {
  hasTitle?: boolean;
  labelText: string;
  value?: string;
  hasError?: boolean;
  isLoading: boolean;
  errorMessage: string;
  onApplyClick: (inputText: string) => void;
  onRemoveClick: any;
}

const SingleInputField: React.FC<IProps> = ({
  hasError,
  isLoading,
  value,
  errorMessage,
  onApplyClick,
  onRemoveClick,
  labelText,
}) => {
  const { t } = useTranslation();
  const rtl = isRTL();

  const [textToShow, setTextToShow] = useState<string>(value || '');

  useEffect(() => {
    setTextToShow(value || '');
  }, [value]);

  const apply = () => {
    if (textToShow.length > 0) {
      onApplyClick(textToShow);
    }
  };

  const remove = () => {
    onRemoveClick();
  };

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setTextToShow(e.target.value.trim());
    } catch {
      // suppress any errors that get fired for .trim on undefined
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <Box p={[2, 3]} bg="white">
        <Flex alignItems="center">
          <Box flex="1" mr={rtl ? 0 : 3} ml={!rtl ? 0 : 3}>
            <Input
              label={t(labelText)}
              value={textToShow}
              onChange={onTextChange}
              disabled={value || isLoading ? true : false}
              variant={value ? 'success' : hasError ? 'error' : 'primary'}
              icon={value && <IconChecked />}
              testid="single-input-entry"
            />
          </Box>
          <Box mt={2}>
            {value ? (
              <Button
                variant="link"
                p={2}
                onClick={remove}
                fontSize={1}
                testid="single-input-cta-cancel"
              >
                {t('remove').toUpperCase()}
              </Button>
            ) : (
              <Button
                variant="link"
                p={2}
                onClick={apply}
                fontSize={1}
                disabled={!textToShow}
                testid="single-input-cta-apply"
              >
                {t('apply')}
              </Button>
            )}
          </Box>
        </Flex>
        {hasError && (
          <Paragraph color={COLORS.RED} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
            {errorMessage}
          </Paragraph>
        )}
      </Box>
      {isLoading && <LinearProgress position="bottom" />}
    </Box>
  );
};

export default SingleInputField;
